import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #mobileMenuObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onMobileAccordionClick);
    this.addEventListener('keydown', this.#onMobileAccordionKeyDown);
    this.#setupAnimatedElementListeners();
    this.#enhanceMobileNavigation();
    this.#mobileMenuObserver = new MutationObserver(() => {
      if (!this.querySelector('.gkikz-mobile-accordion__toggle')) {
        this.#enhanceMobileNavigation();
      }
    });
    this.#mobileMenuObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onMobileAccordionClick);
    this.removeEventListener('keydown', this.#onMobileAccordionKeyDown);
    this.#mobileMenuObserver?.disconnect();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #onMobileAccordionClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('.gkikz-mobile-accordion__toggle');
    if (!(toggle instanceof HTMLAnchorElement) || !this.contains(toggle)) return;

    event.preventDefault();
    this.#toggleMobileAccordion(toggle);
  };

  #onMobileAccordionKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('.gkikz-mobile-accordion__toggle');
    if (!(toggle instanceof HTMLAnchorElement) || !this.contains(toggle)) return;

    event.preventDefault();
    this.#toggleMobileAccordion(toggle);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.#resetMobileAccordions();

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Convert the flat mobile-only navigation groups into compact accordions.
   * Desktop uses a separate header presentation and remains unchanged.
   */
  #enhanceMobileNavigation() {
    const navigation = this.querySelector('.menu-drawer__navigation');
    if (!(navigation instanceof HTMLElement)) return;
    if (
      navigation.dataset.gkikzMobileMenu === 'ready' &&
      navigation.querySelector('.gkikz-mobile-accordion__toggle')
    ) {
      return;
    }

    navigation.dataset.gkikzMobileMenu = 'ready';
    installMobileMenuStyles();

    const topLevelItems = navigation.querySelectorAll(
      '.menu-drawer__menu > .menu-drawer__list-item--flat'
    );

    topLevelItems.forEach((item, index) => {
      const parentLink = Array.from(item.children).find(
        (element) =>
          element instanceof HTMLAnchorElement &&
          element.classList.contains('menu-drawer__menu-item--mainlist')
      );
      const childList = Array.from(item.children).find(
        (element) =>
          element instanceof HTMLUListElement &&
          element.classList.contains('menu-drawer__menu--childlist')
      );

      if (!(parentLink instanceof HTMLAnchorElement)) return;

      const originalTitle = parentLink.textContent?.trim() ?? '';
      if (originalTitle.toUpperCase() === "NEW ARRIVAL'S") {
        parentLink.textContent = 'NEW ARRIVALS';
      }

      if (!(childList instanceof HTMLUListElement)) return;

      const displayTitle = originalTitle.toLowerCase().includes('our story')
        ? 'ABOUT & HELP'
        : originalTitle;
      const submenuId = `GkikzMobileSubmenu-${index + 1}`;
      const indicator = document.createElement('span');

      item.classList.add('gkikz-mobile-accordion');
      childList.id = submenuId;
      childList.classList.add('gkikz-mobile-accordion__content');
      childList.setAttribute('aria-hidden', 'true');
      childList.inert = true;

      parentLink.dataset.gkikzOriginalHref = parentLink.getAttribute('href') ?? '';
      parentLink.removeAttribute('href');
      parentLink.setAttribute('role', 'button');
      parentLink.setAttribute('tabindex', '0');
      parentLink.classList.add('gkikz-mobile-accordion__toggle');
      parentLink.setAttribute('aria-expanded', 'false');
      parentLink.setAttribute('aria-controls', submenuId);
      parentLink.setAttribute('aria-label', `Open ${displayTitle} menu`);
      parentLink.textContent = displayTitle;

      indicator.className = 'gkikz-mobile-accordion__indicator';
      indicator.setAttribute('aria-hidden', 'true');
      parentLink.append(indicator);
    });
  }

  /**
   * Toggle a mobile accordion from the delegated drawer event.
   * @param {HTMLAnchorElement} toggle
   */
  #toggleMobileAccordion(toggle) {
    const item = toggle.closest('.gkikz-mobile-accordion');
    const childList = item?.querySelector('.gkikz-mobile-accordion__content');
    if (!(item instanceof HTMLLIElement) || !(childList instanceof HTMLUListElement)) return;

    const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
    const displayTitle = toggle.childNodes[0]?.textContent?.trim() ?? toggle.textContent?.trim() ?? '';
    this.#resetMobileAccordions();

    if (!willOpen) return;

    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', `Close ${displayTitle} menu`);
    childList.setAttribute('aria-hidden', 'false');
    childList.inert = false;
  }

  /**
   * Collapse every enhanced mobile group except an optional active toggle.
   */
  #resetMobileAccordions() {
    this.querySelectorAll('.gkikz-mobile-accordion__toggle').forEach((toggle) => {
      const item = toggle.closest('.gkikz-mobile-accordion');
      const childList = item?.querySelector('.gkikz-mobile-accordion__content');
      const title = toggle.textContent?.trim() ?? '';

      item?.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', `Open ${title} menu`);

      if (childList instanceof HTMLUListElement) {
        childList.setAttribute('aria-hidden', 'true');
        childList.inert = true;
      }
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

function installMobileMenuStyles() {
  if (document.getElementById('gkikz-mobile-menu-styles')) return;

  const style = document.createElement('style');
  style.id = 'gkikz-mobile-menu-styles';
  style.textContent = `
    @media screen and (max-width: 749px) {
      header-drawer .menu-drawer {
        background: rgba(255, 255, 255, 0.3) !important;
        border: 1px solid rgba(255, 255, 255, 0.14) !important;
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.32),
          0 2px 2px rgba(0, 0, 0, 0.1),
          0 18px 50px rgba(0, 0, 0, 0.16) !important;
        backdrop-filter: blur(33px) saturate(1.08) !important;
        -webkit-backdrop-filter: blur(33px) saturate(1.08) !important;
        overflow: hidden !important;
      }

      header-drawer .menu-drawer__backdrop {
        background: rgba(0, 0, 0, 0.2) !important;
        backdrop-filter: blur(10px) saturate(1.08) !important;
        -webkit-backdrop-filter: blur(10px) saturate(1.08) !important;
      }

      header-drawer .menu-drawer__navigation {
        height: 100% !important;
        padding: 88px 0 28px !important;
        overflow-y: auto !important;
        overscroll-behavior: contain;
        scrollbar-width: none;
      }

      header-drawer .menu-drawer__navigation::-webkit-scrollbar {
        display: none;
      }

      header-drawer .menu-drawer__menu {
        padding-inline: 26px !important;
        margin-block: 0 !important;
      }

      header-drawer .menu-drawer__menu > .menu-drawer__list-item--flat {
        min-height: 0 !important;
        margin: 0 !important;
        border-top: 1px solid rgba(20, 20, 20, 0.12);
      }

      header-drawer .menu-drawer__menu > .menu-drawer__list-item--flat:first-child {
        border-top: 0;
      }

      header-drawer .menu-drawer__menu-item--mainlist,
      header-drawer .gkikz-mobile-accordion__toggle {
        min-height: 58px !important;
        width: 100%;
        padding: 0 !important;
        font-family: var(--menu-top-level-font-family);
        font-size: 1.4rem !important;
        font-style: var(--menu-top-level-font-style);
        font-weight: var(--menu-top-level-font-weight);
        line-height: 1.08 !important;
        letter-spacing: 0;
        text-transform: var(--menu-top-level-font-case);
        color: var(--menu-top-level-font-color);
        text-align: left;
      }

      header-drawer .menu-drawer__menu-item--mainlist {
        display: flex !important;
        align-items: center;
      }

      header-drawer .gkikz-mobile-accordion__toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border: 0;
        border-radius: 0;
        background: transparent;
        box-shadow: none !important;
        appearance: none;
        cursor: pointer;
        color: rgba(3, 3, 2, 0.96) !important;
        outline: none !important;
      }

      header-drawer .gkikz-mobile-accordion__toggle:focus-visible {
        outline: none !important;
        background: rgba(255, 255, 255, 0.16);
      }

      header-drawer .gkikz-mobile-accordion__indicator {
        position: relative;
        width: 18px;
        height: 18px;
        flex: 0 0 18px;
        margin-left: 16px;
      }

      header-drawer .gkikz-mobile-accordion__indicator::before,
      header-drawer .gkikz-mobile-accordion__indicator::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 14px;
        height: 1px;
        background: currentColor;
        transform: translate(-50%, -50%);
        transition: transform 180ms ease, opacity 180ms ease;
      }

      header-drawer .gkikz-mobile-accordion__indicator::after {
        transform: translate(-50%, -50%) rotate(90deg);
      }

      header-drawer .gkikz-mobile-accordion.is-open .gkikz-mobile-accordion__indicator::after {
        opacity: 0;
        transform: translate(-50%, -50%) rotate(0deg);
      }

      header-drawer .gkikz-mobile-accordion__content {
        display: block !important;
        max-height: 0;
        margin: 0 !important;
        padding: 0 0 0 12px !important;
        overflow: hidden;
        opacity: 0;
        visibility: hidden;
        transition: max-height 260ms ease, opacity 180ms ease, padding-bottom 260ms ease;
      }

      header-drawer .gkikz-mobile-accordion.is-open > .gkikz-mobile-accordion__content {
        max-height: 520px;
        padding-bottom: 12px !important;
        opacity: 1;
        visibility: visible;
      }

      header-drawer .gkikz-mobile-accordion__content .menu-drawer__list-item {
        min-height: 44px !important;
        margin: 0 !important;
      }

      header-drawer .gkikz-mobile-accordion__content .menu-drawer__menu-item {
        min-height: 44px;
        width: 100%;
        padding: 8px 0 !important;
        font-family: var(--menu-child-font-family);
        font-size: 0.98rem !important;
        line-height: 1.25;
        letter-spacing: 0;
      }

      header-drawer .menu-drawer__close-button {
        z-index: 2;
        min-width: 44px;
        min-height: 44px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      header-drawer .gkikz-mobile-accordion__content,
      header-drawer .gkikz-mobile-accordion__indicator::before,
      header-drawer .gkikz-mobile-accordion__indicator::after {
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}
